import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import OvCompactButton from '../atoms/OvCompactButton';
import OvTextField from '../atoms/OvTextField';
import OvVideoThumbnail from '../atoms/OvVideoThumbnail';
import OvVideoOrientationField from './OvVideoOrientationField';
import { StagedVideo } from '../../../common/model/type/video-file.types';
import { formatMegabytes } from '../../../common/utils/video-file-utils';
import {
  needsOrientation,
  stagedFieldId,
  stagedFieldsId,
} from '../../../common/utils/video-staging-utils';
import { useVideoStagingSession } from '../../../hooks/use-video-staging-session';
import Colours from '../../../design-system/colours';
import Variables from '../../../design-system/variables';

interface OvStagedVideoCellProps {
  item: StagedVideo;
}

const OvStagedVideoCell: FC<OvStagedVideoCellProps> = ({ item }) => {
  const { t } = useTranslation();
  const session = useVideoStagingSession();
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const { key, file, title, description, dimensions, orientation, thumbnail } =
    item;
  const detailsId = `${stagedFieldsId(key)}-details`;
  const isTitleMissing = !title.trim();
  const isDisabled = session.isSubmitting;

  return (
    <Cell>
      <Main>
        <OvVideoThumbnail
          src={thumbnail === 'reading' ? null : thumbnail}
          placeholderLabel={
            thumbnail === 'reading'
              ? t('videos.staging.loadingPreview')
              : t('videos.staging.previewUnavailable')
          }
        />
        <Summary>
          <OvTextField
            id={stagedFieldId(key, 'title')}
            size="small"
            label={t('videos.fields.title')}
            value={title}
            disabled={isDisabled}
            required
            error={isTitleMissing}
            helperText={
              isTitleMissing ? t('videos.staging.titleRequired') : undefined
            }
            onChange={(event) => session.setTitle(key, event.target.value)}
          />
          <Meta>
            {file.name} ·{' '}
            {t('videos.staging.megabytes', {
              size: formatMegabytes(file.size),
            })}
          </Meta>
        </Summary>
      </Main>

      {dimensions === 'reading' && (
        <Meta>{t('videos.staging.readingSize')}</Meta>
      )}
      {needsOrientation(item) && (
        <OvVideoOrientationField
          itemKey={key}
          orientation={orientation}
          isDisabled={isDisabled}
          onChange={session.setOrientation}
        />
      )}

      <div>
        <OvCompactButton
          tone="secondary"
          aria-expanded={isDetailsOpen}
          aria-controls={detailsId}
          onClick={() => setIsDetailsOpen((open) => !open)}
        >
          {t('videos.staging.moreDetails')}
        </OvCompactButton>
      </div>
      {isDetailsOpen && (
        <Details id={detailsId}>
          <OvTextField
            size="small"
            label={t('videos.fields.description')}
            value={description}
            disabled={isDisabled}
            multiline
            onChange={(event) =>
              session.setDescription(key, event.target.value)
            }
          />
          {dimensions && dimensions !== 'reading' && (
            <Meta>
              {t('videos.staging.size', {
                width: dimensions.width,
                height: dimensions.height,
              })}
            </Meta>
          )}
        </Details>
      )}
    </Cell>
  );
};

export default OvStagedVideoCell;

const Cell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-width: 16rem;
`;

const Main = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: ${Variables.spacing.SMALL};
`;

const Summary = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 10rem;
`;

const Meta = styled.span`
  font-size: ${Variables.fontSizes.SMALL};
  color: ${Colours.OV_CHARCOAL};
  overflow-wrap: anywhere;
`;

const Details = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Variables.spacing.SMALL};
  padding-left: ${Variables.spacing.MEDIUM};
  border-left: 2px solid ${Colours.OV_BORDER_COLOR_V3};
`;
